<template>
	<v-layout class="top-bar">
		<v-toolbar-title class="d-flex align-center"> </v-toolbar-title>

		<!-- Add Voice Bot -->
		<v-btn
			v-if="debugStore.altKeyHeld && domainStore.hasAccessRights(UserRole.Editor)"
			:disabled="alternisNewStore.voiceBotExists"
			variant="text"
			icon="mdi-robot"
			:loading="isCreatingVoiceBot"
			@click.stop="createVoiceBot"
		/>

		<!-- Alternis "Fake" Call Workaround -->
		<v-btn
			v-if="debugStore.altKeyHeld && domainStore.hasAccessRights(UserRole.Editor)"
			variant="text"
			icon="mdi-phone-cancel"
			:loading="isEnding"
			:disabled="!alternisNewStore.sseConnected"
			@click.stop="endSession"
		/>

		<!-- DEBUG BUTTON-->
		<v-btn
			v-if="showDebugBtn"
			:variant="debugStore.debugMode ? 'outlined' : 'text'"
			icon="mdi-bug"
			:color="debugStore.debugMode ? 'red' : ''"
			@click.stop="toggleDebugMode"
		></v-btn>

		<!-- SETTINGS BUTTON -->
		<v-menu v-if="domainStore.hasAccessRights(UserRole.Editor)" :close-on-content-click="false" location="end">
			<template v-slot:activator="{ props }">
				<v-btn variant="text" icon="mdi-cog" v-bind="props"></v-btn>
			</template>
			<v-card min-width="250" elevation="3">
				<v-tooltip location="start">
					<template v-slot:activator="{ props }">
						<div v-bind="props">
							<v-select
								v-if="domainStore.isAdmin"
								v-model="displayedSuggestionModel"
								:label="symbolsStore.GUILabel('45fc6ce1-101f-47ba-a8ac-de8c593f026b')"
								:items="configStore.suggestionModels"
								hide-details
								prepend-icon="mdi-cube-outline"
								class="mx-2"
								clearable
								@click:clear="
									configStore.resetUserSuggestionModel(
										domainStore.domain.id,
										dataspaceStore.openedDataspaceId || '',
									)
								"
							>
								<template v-slot:selection="{ item }">
									<v-icon
										v-if="
											item.value ===
											configStore.userSuggestionModels[domainStore.domain.id]?.[
												dataspaceStore.openedDataspaceId || ''
											]
										"
										size="small"
										class="mr-1"
									>
										mdi-account
									</v-icon>
									<v-icon
										v-else-if="item.value === configStore.dataspaceSuggestionModel"
										size="small"
										class="mr-1"
									>
										mdi-database
									</v-icon>
									<v-icon
										v-else-if="item.value === configStore.defaultSuggestionModel"
										size="small"
										class="mr-1"
									>
										mdi-earth
									</v-icon>
									{{ item.value }}
								</template>
								<template v-slot:item="{ item, props: itemProps }">
									<v-list-item v-bind="itemProps">
										<template v-slot:title>
											<v-icon
												v-if="
													item.value ===
													configStore.userSuggestionModels[domainStore.domain.id]?.[
														dataspaceStore.openedDataspaceId || ''
													]
												"
												size="small"
												class="mr-1"
											>
												mdi-account
											</v-icon>
											<v-icon
												v-else-if="item.value === configStore.dataspaceSuggestionModel"
												size="small"
												class="mr-1"
											>
												mdi-database
											</v-icon>
											<v-icon
												v-else-if="item.value === configStore.defaultSuggestionModel"
												size="small"
												class="mr-1"
											>
												mdi-earth
											</v-icon>
											{{ item.value }}
										</template>
									</v-list-item>
								</template>
							</v-select>
						</div>
					</template>
					<span>{{
						configStore.getSuggestionModelSourceText(
							domainStore.domain.id,
							dataspaceStore.openedDataspaceId || "",
						)
					}}</span>
				</v-tooltip>
			</v-card>
		</v-menu>
	</v-layout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useConfigStore, type SuggestionModel } from "../../../../store/alternis/config";
import { useDomainStore } from "../../../../store/domain";
import { useInstanceDataspacesStore } from "../../../../store/i-dataspace";
import { useDebugStore } from "../../../../store/debug";
import { useSymbolsStore } from "../../../../store/symbols";
import { useInstanceWorkspacesStore } from "../../../../store/i-workspace";
import { useAlternisNewStore } from "../../../../store/alternis_new";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";

const configStore = useConfigStore();
const domainStore = useDomainStore();
const dataspaceStore = useInstanceDataspacesStore();
const symbolsStore = useSymbolsStore();
const debugStore = useDebugStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const alternisNewStore = useAlternisNewStore();

const showDebugBtn = computed(() => debugStore.debugMode || (domainStore.isEditorOrAdmin && debugStore.altKeyHeld));
const isEnding = ref(false);
const isCreatingVoiceBot = ref(false);

const displayedSuggestionModel = computed({
	get: () => configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || ""),
	set: (val: SuggestionModel) => {
		configStore.setUserSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || "", val);
	},
});

window.addEventListener("keydown", (e) => {
	if (e.altKey) debugStore.setAltKeyHeld(true);
});
window.addEventListener("keyup", (e) => {
	if (!e.altKey) debugStore.setAltKeyHeld(false);
});

function toggleDebugMode() {
	debugStore.toggleDebugMode();
}

async function endSession() {
	if (!instanceWorkspacesStore.openedWorkspaceId) return;
	isEnding.value = true;
	await alternisNewStore.endSession(domainStore.domain.id, instanceWorkspacesStore.openedWorkspaceId);
	isEnding.value = false;
}

async function createVoiceBot() {
	if (!instanceWorkspacesStore.openedWorkspaceId) return;
	isCreatingVoiceBot.value = true;
	await alternisNewStore.createVoiceBot(
		domainStore.domain.id,
		dataspaceStore.openedDataspaceId || "",
		instanceWorkspacesStore.openedWorkspaceId,
	);
	isCreatingVoiceBot.value = false;
}

onMounted(() => {
	if (!instanceWorkspacesStore.openedWorkspaceId) return;
	dataspaceStore.loadEnvironmentVariables(domainStore.domain.id, dataspaceStore.openedDataspaceId || "");
	alternisNewStore.checkVoiceBotExists(
		domainStore.domain.id,
		dataspaceStore.openedDataspaceId || "",
		instanceWorkspacesStore.openedWorkspaceId,
	);
});
</script>

<style scoped>
.top-bar {
	box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.5);
	height: 48px;
	min-height: 48px;
}
</style>
